/**
 * SessionRecorder — the target side for live view AND video log. One rrweb
 * recorder feeds both sinks:
 *  - live view: a superadmin opened a watcher (streams every 3s)
 *  - video log: this user is on the recording list (streams every 5s)
 * At each Toronto midnight the recorder takes a fresh full snapshot so every
 * day's file replays standalone. No UI. Password inputs stay masked.
 */
import { useEffect, useRef } from "react";
import api from "@/lib/api";

const torontoDay = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "America/Toronto", year: "numeric", month: "2-digit", day: "2-digit" })
    .format(new Date());

export default function SessionRecorder({ enabled }) {
  const st = useRef({
    rr: null, stop: null,
    liveSid: null, liveBuffer: [],
    recording: false, recordBuffer: [], day: torontoDay(),
    flushing: false,
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const s = st.current;

    const stopAll = () => {
      try { s.stop?.(); } catch { /* noop */ }
      s.stop = null;
      s.rr = null;
      s.liveSid = null;
      s.recording = false;
      s.liveBuffer = [];
      s.recordBuffer = [];
    };

    const ensureRecorder = async () => {
      if (s.stop) return;
      try {
        s.rr = await import("rrweb");
        s.stop = s.rr.record({
          emit(e) {
            if (s.liveSid) s.liveBuffer.push(e);
            if (s.recording) s.recordBuffer.push(e);
          },
          checkoutEveryNms: 60000,
          recordCanvas: false,
          maskInputOptions: { password: true },
          sampling: { mousemove: 50, mouseInteraction: true, scroll: 150, input: "last" },
        });
      } catch { /* rrweb unavailable */ }
    };

    const flush = async () => {
      if (s.flushing) return;
      s.flushing = true;
      try {
        // new day: fresh full snapshot first so the day-file replays alone
        const today = torontoDay();
        if (s.recording && s.stop && today !== s.day && s.rr?.record?.takeFullSnapshot) {
          s.day = today;
          try { s.rr.record.takeFullSnapshot(); } catch { /* noop */ }
        }
        if (s.liveSid && s.liveBuffer.length) {
          const events = s.liveBuffer;
          s.liveBuffer = [];
          try {
            await api.post(`/liveview/${s.liveSid}/events`, { events });
          } catch (e) {
            if (e?.response?.status === 404) { s.liveSid = null; s.liveBuffer = []; }
            else s.liveBuffer = events.concat(s.liveBuffer);
          }
        }
        if (s.recording && s.recordBuffer.length) {
          const events = s.recordBuffer;
          s.recordBuffer = [];
          try {
            await api.post("/record/push", { events });
          } catch (e) {
            if (e?.response?.status === 403) { s.recording = false; s.recordBuffer = []; }
            else s.recordBuffer = events.concat(s.recordBuffer);
          }
        }
      } finally {
        s.flushing = false;
      }
    };

    const poll = async () => {
      try {
        const [{ data: lv }, { data: rec }] = await Promise.all([
          api.get("/liveview/request").catch(() => ({ data: {} })),
          api.get("/record/request").catch(() => ({ data: {} })),
        ]);
        const sid = lv?.session_id || null;
        const wantRecord = !!rec?.record;
        if (sid !== s.liveSid) { s.liveSid = sid; s.liveBuffer = []; }
        if (wantRecord !== s.recording) { s.recording = wantRecord; s.recordBuffer = []; s.day = torontoDay(); }
        if ((sid || wantRecord) && !s.stop) await ensureRecorder();
        if (!sid && !wantRecord && s.stop) stopAll();
      } catch { /* keep polling */ }
    };

    poll();
    const pollId = setInterval(poll, 15000);
    const flushId = setInterval(flush, 3000);
    return () => {
      clearInterval(pollId);
      clearInterval(flushId);
      stopAll();
    };
  }, [enabled]);

  return null;
}
